import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAppLayoutContextConsumer } from '@occc-app/design-system';
import { useGetCampaignName, getCreatePageLinks } from '@occc-app/routes/CampaignComponents/utils';
import { Offer_Engagement_Type } from '@occc/graphql';
import { OfferType } from '@occc-app/offerValidationService/types';

import { useChainId } from '@occc-app/Chain/hooks';

import { OfferCreateConfigurePage } from '@occc-app/routes/Offer/OfferCreatePage/OfferCreateConfigurePage';
import { Flow_Types } from '@occc-app/offerHelpers/types';
import { CampaignComponentTabs } from '@occc-app/routes/CampaignComponents/components/ConfigurationLayout';
import { useMakeOffersFilter } from '@occc-app/offerHelpers/hooks/use-make-offers-filter.hook';
import { useNavigateToCampaign } from '@occc-app/offerHelpers/hooks/useNavigateToCampaign';
import { PunchcardConfigureFormInitialValues, PunchcardConfigureFormValues } from '../types';
import { PunchcardConfigureForm } from '../components/PunchcardConfigureForm/PunchcardConfigureForm';
import { getConfigureFormOperations } from '../components/PunchcardConfigureForm/utils/configureFormOperations';
import { transformOfferToInitialValues } from '../components/PunchcardConfigureForm/utils/configureFormInitialValues';

import { routeCreator } from '../utils';
import { messages } from '../messages';

export const PunchcardCreatePageContainer = () => {
  const navigate = useNavigate();
  const { id: campaignId = '' } = useParams<{ id: string }>();

  const chainId = useChainId();
  const campaignName = useGetCampaignName(chainId, campaignId);
  const navigateToCampaign = useNavigateToCampaign(campaignId);

  const links = useMemo(
    () => getCreatePageLinks(campaignId, campaignName, 'New Club / Punchcard'),
    [campaignId, campaignName],
  );
  useAppLayoutContextConsumer(links);

  const makeOffersFilter = useMakeOffersFilter();

  return (
    <OfferCreateConfigurePage<PunchcardConfigureFormValues, PunchcardConfigureFormInitialValues>
      pageTitle='Campaign > Create'
      subtitle={messages.subtitle}
      offerType={OfferType.ClubOrPunchcard}
      engagementType={Offer_Engagement_Type.PunchcardChallenge}
      campaignId={campaignId}
      getConfigureOperations={getConfigureFormOperations}
      getInitialValues={transformOfferToInitialValues}
      onOfferCreated={(offerId) =>
        navigate(routeCreator.configureWithTab(campaignId, offerId, CampaignComponentTabs.Target))
      }
      renderConfigureForm={({ onSubmit, initialValues, loading }) => (
        <PunchcardConfigureForm
          onSubmit={onSubmit}
          initialValues={initialValues}
          loading={loading}
          onCancelClick={navigateToCampaign}
          makeOffersFilter={makeOffersFilter}
          currentFlowType={Flow_Types.Configure}
          isFormEditable
        />
      )}
    />
  );
};
